<!-- Hero / Breadcrumb -->
<section class="privacy-hero">
  <div class="container hero-inner">
    <div class="row align-items-center g-5">
      <div class="col-lg-7" data-aos="fade-up" data-aos-duration="1000" data-aos-once="true">
        <span class="breadcrumb-pill">
          <i class="ti ti-shield-lock"></i>
          Privacy Policy
        </span>
        <h1 class="mt-3 mb-3">
          Your data, protected with
          <span class="grad-text">care &amp; transparency</span>
        </h1>
        <p class="text-gray-9 mb-4" style="max-width: 560px">
          At AVR HRMS, privacy isn't a checkbox — it's a promise. Learn exactly what we collect, why
          we collect it, and the controls you have over your information.
        </p>
        <div class="d-flex flex-wrap gap-2">
          <a
            href="#data-we-collect"
            class="btn btn-primary-gradient rounded btn-sm align-items-center"
          >
            <i class="ti ti-database ms-1 me-2"></i>What we collect
            <i class="ti ti-arrow-right ms-2"></i>
          </a>
          <a href="#faq" class="btn btn-secondary rounded btn-sm align-items-center">
            <i class="ti ti-help-circle me-2"></i>Read FAQs
          </a>
        </div>
        <div class="mt-4 d-flex flex-wrap gap-3 text-gray-9" style="font-size: 14px">
          <span><i class="ti ti-lock text-primary me-1"></i> 256-bit encryption</span>
          <span><i class="ti ti-certificate text-primary me-1"></i> GDPR ready</span>
          <span><i class="ti ti-eye-off text-primary me-1"></i> No data selling</span>
        </div>
      </div>
      <div
        class="col-lg-5 hero-art"
        data-aos="zoom-in"
        data-aos-duration="1000"
        data-aos-once="true"
      >
        <div class="art-card text-center">
          <div class="art-icon"><i class="ti ti-shield-check"></i></div>
          <h4 class="mb-2">Privacy by Design</h4>
          <p class="text-gray-9 mb-3">
            Every feature is built around minimal data collection, strict access controls, and full
            <strong class="text-primary">user transparency</strong>.
          </p>
          <div class="badge-row">
            <span class="mini-badge"><i class="ti ti-lock"></i> Encrypted</span>
            <span class="mini-badge"><i class="ti ti-server-2"></i> Secure Servers</span>
            <span class="mini-badge"><i class="ti ti-user-check"></i> Consent-first</span>
            <span class="mini-badge"><i class="ti ti-trash"></i> Right to delete</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</section>
<!-- /Hero -->

<!-- Highlight cards -->
<section class="section-pad">
  <div class="container">
    <div class="text-center mb-5" data-aos="fade-up" data-aos-once="true">
      <span class="section-eyebrow"><i class="ti ti-sparkles"></i> Our Commitments</span>
      <h2 class="mt-3 fw-bold">Privacy principles we live by</h2>
      <p class="text-gray-9 mx-auto" style="max-width: 640px">
        Four promises that guide every decision we make about your information.
      </p>
    </div>
    <div class="row g-4">
      <div class="col-md-6 col-lg-3" data-aos="fade-up" data-aos-delay="100" data-aos-once="true">
        <div class="highlight-card text-center">
          <span class="highlight-icon"><i class="ti ti-lock-square-rounded"></i></span>
          <h5 class="fw-bold">End-to-End Security</h5>
          <p class="text-gray-9 mb-0">
            All data is encrypted in transit (TLS) and at rest (AES-256).
          </p>
        </div>
      </div>
      <div class="col-md-6 col-lg-3" data-aos="fade-up" data-aos-delay="200" data-aos-once="true">
        <div class="highlight-card text-center">
          <span class="highlight-icon alt-1"><i class="ti ti-eye-off"></i></span>
          <h5 class="fw-bold">No Data Selling</h5>
          <p class="text-gray-9 mb-0">We never sell, rent, or trade your personal information.</p>
        </div>
      </div>
      <div class="col-md-6 col-lg-3" data-aos="fade-up" data-aos-delay="300" data-aos-once="true">
        <div class="highlight-card text-center">
          <span class="highlight-icon alt-2"><i class="ti ti-user-check"></i></span>
          <h5 class="fw-bold">Consent-First</h5>
          <p class="text-gray-9 mb-0">
            Optional data is collected only with your explicit consent.
          </p>
        </div>
      </div>
      <div class="col-md-6 col-lg-3" data-aos="fade-up" data-aos-delay="400" data-aos-once="true">
        <div class="highlight-card text-center">
          <span class="highlight-icon alt-3"><i class="ti ti-trash-x"></i></span>
          <h5 class="fw-bold">Right to Erase</h5>
          <p class="text-gray-9 mb-0">Request full deletion of your data at any time.</p>
        </div>
      </div>
    </div>
  </div>
</section>

<!-- Data we collect -->
<section id="data-we-collect" class="privacy-section-bg section-pad">
  <div class="container">
    <div class="text-center mb-5" data-aos="fade-up" data-aos-once="true">
      <span class="section-eyebrow"><i class="ti ti-database"></i> Information we collect</span>
      <h2 class="mt-3 fw-bold">Four categories of data — nothing more</h2>
    </div>
    <div class="row g-4">
      <div class="col-md-6 col-lg-3" data-aos="fade-up" data-aos-delay="100" data-aos-once="true">
        <div class="data-card">
          <span class="data-num">1</span>
          <i class="ti ti-user-circle data-ico"></i>
          <h5 class="fw-bold mt-2">Account Info</h5>
          <p class="text-gray-9 mb-0">Name, email, phone, company — what you provide at sign-up.</p>
        </div>
      </div>
      <div class="col-md-6 col-lg-3" data-aos="fade-up" data-aos-delay="200" data-aos-once="true">
        <div class="data-card">
          <span class="data-num">2</span>
          <i class="ti ti-credit-card data-ico"></i>
          <h5 class="fw-bold mt-2">Billing Data</h5>
          <p class="text-gray-9 mb-0">
            Processed by PCI-DSS compliant gateways. We never store card numbers.
          </p>
        </div>
      </div>
      <div class="col-md-6 col-lg-3" data-aos="fade-up" data-aos-delay="300" data-aos-once="true">
        <div class="data-card">
          <span class="data-num">3</span>
          <i class="ti ti-device-analytics data-ico"></i>
          <h5 class="fw-bold mt-2">Usage Analytics</h5>
          <p class="text-gray-9 mb-0">
            Anonymous metrics that help us improve product performance.
          </p>
        </div>
      </div>
      <div class="col-md-6 col-lg-3" data-aos="fade-up" data-aos-delay="400" data-aos-once="true">
        <div class="data-card">
          <span class="data-num">4</span>
          <i class="ti ti-cookie data-ico"></i>
          <h5 class="fw-bold mt-2">Cookies</h5>
          <p class="text-gray-9 mb-0">
            Essential cookies for login &amp; preferences. Optional ones need consent.
          </p>
        </div>
      </div>
    </div>
  </div>
</section>

<!-- Your Rights + Security visual -->
<section class="section-pad">
  <div class="container">
    <div class="row g-5 align-items-center">
      <div class="col-lg-6" data-aos="fade-right" data-aos-once="true">
        <span class="section-eyebrow"><i class="ti ti-checks"></i> Your Rights</span>
        <h3 class="mt-3 fw-bold">You stay in control of your data</h3>
        <p class="text-gray-9">
          Under GDPR, CCPA, and other regulations, you have these rights — and we make them easy to
          exercise.
        </p>
        <div class="mt-3">
          <div class="right-row">
            <span class="icon-tick"><i class="ti ti-check"></i></span>
            <div>
              <strong>Access your data</strong>
              <p class="text-gray-9 mb-0">
                Download a complete copy of everything we store about you.
              </p>
            </div>
          </div>
          <div class="right-row">
            <span class="icon-tick"><i class="ti ti-check"></i></span>
            <div>
              <strong>Correct inaccuracies</strong>
              <p class="text-gray-9 mb-0">
                Edit account details directly or request changes via support.
              </p>
            </div>
          </div>
          <div class="right-row">
            <span class="icon-tick"><i class="ti ti-check"></i></span>
            <div>
              <strong>Delete your account</strong>
              <p class="text-gray-9 mb-0">Permanent erasure within 30 days — no questions asked.</p>
            </div>
          </div>
          <div class="right-row">
            <span class="icon-tick"><i class="ti ti-check"></i></span>
            <div>
              <strong>Withdraw consent</strong>
              <p class="text-gray-9 mb-0">
                Opt out of marketing or analytics tracking at any time.
              </p>
            </div>
          </div>
          <div class="right-row">
            <span class="icon-tick"><i class="ti ti-check"></i></span>
            <div>
              <strong>Data portability</strong>
              <p class="text-gray-9 mb-0">
                Export your data in machine-readable JSON or CSV format.
              </p>
            </div>
          </div>
        </div>
      </div>
      <div class="col-lg-6" data-aos="fade-left" data-aos-once="true">
        <div class="security-visual text-center">
          <span class="ring r1"></span>
          <span class="ring r2"></span>
          <div
            class="art-icon mx-auto"
            style="
              width: 96px;
              height: 96px;
              border-radius: 24px;
              display: flex;
              align-items: center;
              justify-content: center;
              background: linear-gradient(135deg, #6366f1, #8b5cf6);
              color: #fff;
              font-size: 46px;
              box-shadow: 0 18px 30px rgba(99, 102, 241, 0.35);
              margin-bottom: 18px;
            "
          >
            <i class="ti ti-shield-lock"></i>
          </div>
          <h4 class="fw-bold">How we protect you</h4>
          <p class="text-gray-9">
            Multiple layers of security keep your information safe — every minute of every day.
          </p>
          <div class="mt-3">
            <span class="sec-pill"><i class="ti ti-lock"></i> AES-256 Encryption</span>
            <span class="sec-pill indigo"><i class="ti ti-server-2"></i> ISO-27001 Servers</span>
            <span class="sec-pill green"><i class="ti ti-shield-check"></i> SOC 2 Aligned</span>
            <span class="sec-pill"><i class="ti ti-key"></i> 2FA Available</span>
            <span class="sec-pill indigo"><i class="ti ti-bug"></i> Regular Audits</span>
            <span class="sec-pill green"><i class="ti ti-clock-shield"></i> 24/7 Monitoring</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</section>

<!-- How we use & sharing -->
<section class="section-pad" style="background: #fafbff">
  <div class="container">
    <div class="row g-5 align-items-start">
      <div class="col-lg-6" data-aos="fade-up" data-aos-once="true">
        <span class="section-eyebrow"><i class="ti ti-bulb"></i> How we use it</span>
        <h3 class="mt-3 fw-bold">What we do with your data</h3>
        <div class="mt-3">
          <div class="right-row">
            <span class="icon-shield"><i class="ti ti-settings"></i></span>
            <div>
              <strong>Operate the service</strong>
              <p class="text-gray-9 mb-0">
                Authentication, payroll processing, attendance tracking.
              </p>
            </div>
          </div>
          <div class="right-row">
            <span class="icon-shield"><i class="ti ti-headset"></i></span>
            <div>
              <strong>Provide support</strong>
              <p class="text-gray-9 mb-0">Resolve issues and answer your questions efficiently.</p>
            </div>
          </div>
          <div class="right-row">
            <span class="icon-shield"><i class="ti ti-chart-line"></i></span>
            <div>
              <strong>Improve features</strong>
              <p class="text-gray-9 mb-0">Aggregated analytics guide product roadmap decisions.</p>
            </div>
          </div>
          <div class="right-row">
            <span class="icon-shield"><i class="ti ti-mail-check"></i></span>
            <div>
              <strong>Communicate updates</strong>
              <p class="text-gray-9 mb-0">
                Important notices, security alerts, and (opt-in) newsletters.
              </p>
            </div>
          </div>
        </div>
      </div>
      <div class="col-lg-6" data-aos="fade-up" data-aos-delay="150" data-aos-once="true">
        <span class="section-eyebrow" style="background: #eef0ff; color: #6366f1">
          <i class="ti ti-share"></i>
          Sharing
        </span>
        <h3 class="mt-3 fw-bold">When we share data — and with whom</h3>
        <div class="mt-3">
          <div class="right-row">
            <span class="icon-shield"><i class="ti ti-cloud"></i></span>
            <div>
              <strong>Trusted infrastructure providers</strong>
              <p class="text-gray-9 mb-0">Hosting &amp; storage partners under strict DPAs.</p>
            </div>
          </div>
          <div class="right-row">
            <span class="icon-shield"><i class="ti ti-credit-card"></i></span>
            <div>
              <strong>Payment processors</strong>
              <p class="text-gray-9 mb-0">Only what's needed to complete a transaction.</p>
            </div>
          </div>
          <div class="right-row">
            <span class="icon-shield"><i class="ti ti-gavel"></i></span>
            <div>
              <strong>Legal compliance</strong>
              <p class="text-gray-9 mb-0">
                When required by valid legal process — and we'll notify you when allowed.
              </p>
            </div>
          </div>
          <div class="right-row">
            <span class="icon-shield"><i class="ti ti-x"></i></span>
            <div>
              <strong>Never with advertisers</strong>
              <p class="text-gray-9 mb-0">
                We do not share, sell, or rent personal data for ad targeting.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</section>

<!-- FAQ -->
<section id="faq" class="section-pad">
  <div class="container">
    <div class="text-center mb-5" data-aos="fade-up" data-aos-once="true">
      <span class="section-eyebrow"><i class="ti ti-help"></i> FAQs</span>
      <h2 class="mt-3 fw-bold">Privacy — frequently asked</h2>
    </div>
    <div class="row justify-content-center">
      <div class="col-lg-9" data-aos="fade-up" data-aos-once="true">
        <div class="accordion privacy-faq" id="privacyFaq">
          <div class="accordion-item">
            <h2 class="accordion-header">
              <button
                class="accordion-button"
                type="button"
                data-bs-toggle="collapse"
                data-bs-target="#pfaq1"
              >
                <i class="ti ti-database me-2"></i>
                What personal data do you collect?
              </button>
            </h2>
            <div id="pfaq1" class="accordion-collapse collapse show" data-bs-parent="#privacyFaq">
              <div class="accordion-body">
                Account info (name, email, phone, company), billing details processed by certified
                gateways, anonymous usage analytics, and essential cookies. We collect
                <strong>only what's necessary</strong>
                to deliver the service.
              </div>
            </div>
          </div>
          <div class="accordion-item">
            <h2 class="accordion-header">
              <button
                class="accordion-button collapsed"
                type="button"
                data-bs-toggle="collapse"
                data-bs-target="#pfaq2"
              >
                <i class="ti ti-eye-off me-2"></i>
                Do you sell my data to third parties?
              </button>
            </h2>
            <div id="pfaq2" class="accordion-collapse collapse" data-bs-parent="#privacyFaq">
              <div class="accordion-body">
                <strong>Never.</strong>
                We do not sell, rent, or trade your personal information to advertisers, data
                brokers, or any third party for marketing purposes.
              </div>
            </div>
          </div>
          <div class="accordion-item">
            <h2 class="accordion-header">
              <button
                class="accordion-button collapsed"
                type="button"
                data-bs-toggle="collapse"
                data-bs-target="#pfaq3"
              >
                <i class="ti ti-trash me-2"></i>
                How can I delete my account &amp; data?
              </button>
            </h2>
            <div id="pfaq3" class="accordion-collapse collapse" data-bs-parent="#privacyFaq">
              <div class="accordion-body">
                Email
                <a href="mailto:privacy@avrhrms.com" class="text-primary fw-semibold"
                  >privacy&#64;avrhrms.com</a
                >
                with your account email. We process deletion within
                <strong>30 days</strong>
                and send a confirmation when complete.
              </div>
            </div>
          </div>
          <div class="accordion-item">
            <h2 class="accordion-header">
              <button
                class="accordion-button collapsed"
                type="button"
                data-bs-toggle="collapse"
                data-bs-target="#pfaq4"
              >
                <i class="ti ti-cookie me-2"></i>
                Can I disable cookies?
              </button>
            </h2>
            <div id="pfaq4" class="accordion-collapse collapse" data-bs-parent="#privacyFaq">
              <div class="accordion-body">
                Yes. You can disable optional analytics &amp; preference cookies via the cookie
                banner or your browser settings.
                <strong>Essential cookies</strong>
                (login, security) are required for the service to work.
              </div>
            </div>
          </div>
          <div class="accordion-item">
            <h2 class="accordion-header">
              <button
                class="accordion-button collapsed"
                type="button"
                data-bs-toggle="collapse"
                data-bs-target="#pfaq5"
              >
                <i class="ti ti-world me-2"></i>
                Where is my data stored?
              </button>
            </h2>
            <div id="pfaq5" class="accordion-collapse collapse" data-bs-parent="#privacyFaq">
              <div class="accordion-body">
                Data is hosted on ISO-27001 certified servers in secure data centers. International
                transfers are protected by Standard Contractual Clauses where applicable.
              </div>
            </div>
          </div>
          <div class="accordion-item">
            <h2 class="accordion-header">
              <button
                class="accordion-button collapsed"
                type="button"
                data-bs-toggle="collapse"
                data-bs-target="#pfaq6"
              >
                <i class="ti ti-bell me-2"></i>
                How will I be notified of policy changes?
              </button>
            </h2>
            <div id="pfaq6" class="accordion-collapse collapse" data-bs-parent="#privacyFaq">
              <div class="accordion-body">
                Material changes are announced via email and a prominent banner inside your
                dashboard at least
                <strong>30 days</strong>
                before they take effect.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</section>

<!-- CTA -->
<section class="section-pad">
  <div class="container">
    <div class="privacy-cta text-center" data-aos="zoom-in" data-aos-once="true">
      <i class="ti ti-shield-heart" style="font-size: 54px; color: #ffb38a"></i>
      <h2 class="mt-3 mb-2" style="color: #ffffff">Questions about your privacy?</h2>
      <p class="mb-4" style="opacity: 0.85; max-width: 560px; margin-inline: auto">
        Our Data Protection team is here to help. Reach out for any privacy concern, request, or
        clarification.
      </p>
      <div class="d-flex flex-wrap gap-2 justify-content-center">
        <a
          href="mailto:privacy@avrhrms.com"
          class="btn btn-primary-gradient rounded btn-sm align-items-center"
        >
          <i class="ti ti-mail me-2"></i>
          Email Privacy Team
          <i class="ti ti-arrow-right ms-2"></i>
        </a>
        <a
          href="https://wa.me/918107011186"
          target="_blank"
          class="btn btn-secondary rounded btn-sm align-items-center"
        >
          <i class="fa-brands fa-whatsapp me-2"></i>
          Chat on WhatsApp
        </a>
      </div>
    </div>
  </div>
</section>
